'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef } from 'react';

type ScrollOptions = {
  selector: string;
  storageKey?: string;
};

const SCROLL_THRESHOLD = 5;

export const useScrollRestoration = ({
  selector,
  storageKey
}: ScrollOptions) => {
  const pathname = usePathname();
  const key = storageKey || 'channelsScrollPosition';
  const prevPathRef = useRef<string | null>(null);
  const scrollTopRef = useRef<number | null>(null);
  const hasRestoredRef = useRef<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    // prevents flicker
    const style = document.createElement('style');
    style.innerHTML = `
      ${selector} {
        -ms-overflow-style: none !important;
        scrollbar-width: none !important;
      }
      ${selector}::-webkit-scrollbar {
        display: none !important;
      }
    `;
    styleRef.current = style;

    document.head.appendChild(style);

    return () => {
      if (style.parentNode) {
        style.remove();
      }
    };
  }, []);

  useEffect(() => {
    const isPathChanged =
      prevPathRef.current && prevPathRef.current !== pathname;

    if (isPathChanged) {
      hasRestoredRef.current = false;

      if (styleRef.current && !styleRef.current.parentNode) {
        document.head.appendChild(styleRef.current);
      }
    }

    prevPathRef.current = pathname;
  }, [pathname]);

  useLayoutEffect(() => {
    if (hasRestoredRef.current) return;

    const performScrollRestore = () => {
      const el = document.querySelector<HTMLElement>(selector);

      if (!el) return false;

      elementRef.current = el;

      const saved = localStorage.getItem(key);

      if (!saved) {
        hasRestoredRef.current = true;
        return true;
      }

      try {
        const { scrollTop } = JSON.parse(saved);

        scrollTopRef.current = scrollTop;

        if (scrollTop <= SCROLL_THRESHOLD) {
          hasRestoredRef.current = true;
          return true;
        }

        if (elementRef.current) {
          elementRef.current.scrollTop = scrollTopRef.current!;

          setTimeout(() => {
            if (styleRef.current && styleRef.current.parentNode) {
              styleRef.current.parentNode.removeChild(styleRef.current);
            }

            hasRestoredRef.current = true;
          }, 200);
        }

        return true;
      } catch (e) {
        console.error('Failed to restore scroll position:', e);
        hasRestoredRef.current = true;
        return true;
      }
    };

    if (!performScrollRestore()) {
      Promise.resolve().then(performScrollRestore);
    }
  }, [selector, key, pathname]);

  useEffect(() => {
    const el = document.querySelector<HTMLElement>(selector);

    if (!el) return;

    if (!elementRef.current) {
      elementRef.current = el;
    }

    const handleScroll = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (!elementRef.current) return;

        const currentScrollTop = elementRef.current.scrollTop;

        if (
          !scrollTopRef.current ||
          Math.abs(scrollTopRef.current - currentScrollTop) > SCROLL_THRESHOLD
        ) {
          scrollTopRef.current = currentScrollTop;

          try {
            localStorage.setItem(
              key,
              JSON.stringify({ scrollTop: currentScrollTop })
            );
          } catch (e) {
            console.error('Failed to save scroll position:', e);
          }
        }

        timeoutRef.current = null;
      }, 150);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (elementRef.current) {
        const finalScrollTop = elementRef.current.scrollTop;

        if (finalScrollTop > 0) {
          try {
            localStorage.setItem(
              key,
              JSON.stringify({ scrollTop: finalScrollTop })
            );
          } catch (e) {
            console.error('Failed to save final scroll position:', e);
          }
        }

        elementRef.current.removeEventListener('scroll', handleScroll);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }
    };
  }, [selector, key]);
};
